import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  settings: "Settings",
  dashboards: "Dashboards",
  description:
    "Choose which widgets users see on each dashboard, and in what order. Changes save as you switch and drag.",
  userDashboard: "User Dashboard",
  performanceDashboard: "Performance Dashboard",
  userVisibility: "Visible for all users — their own clients and work.",
  performanceVisibility: "Visible for users with admin permission.",
  complianceOnly: "Compliance only",
  userComplianceNote: "widgets are shown only to users who also have compliance permission.",
  performanceComplianceNote:
    "widgets are shown only to admins who also have compliance permission.",
  keyFigures: "Key figures",
  keyFiguresDescription:
    "Small cards in one row at the top, four to a row. Drag to set their order.",
  sections: "Sections",
  sectionsDescription: "Panels below the key figures, in a 3-column grid. Drag to set their order.",
  alwaysShown:
    "A widget that is switched on is always shown, with an empty state when there is no data.",
  userOnlyNote:
    "Next meetings and Cash to invest belong to one user's clients, so they are set up on the User Dashboard tab only.",
  saveFailed: "Couldn't save the change.",
  unableToLoad: "Unable to load the dashboard setup.",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    settings: "Paramètres",
    dashboards: "Tableaux de bord",
    description:
      "Choisissez les widgets que les utilisateurs voient sur chaque tableau de bord, et dans quel ordre. Les modifications sont enregistrées dès que vous activez un widget ou le déplacez.",
    userDashboard: "Tableau de bord utilisateur",
    performanceDashboard: "Tableau de bord des performances",
    userVisibility: "Visible par tous les utilisateurs — leurs propres clients et leur travail.",
    performanceVisibility: "Visible par les utilisateurs disposant du droit administrateur.",
    complianceOnly: "Conformité uniquement",
    userComplianceNote:
      "Ces widgets ne sont affichés qu'aux utilisateurs disposant aussi du droit conformité.",
    performanceComplianceNote:
      "Ces widgets ne sont affichés qu'aux administrateurs disposant aussi du droit conformité.",
    keyFigures: "Chiffres clés",
    keyFiguresDescription:
      "Petites cartes alignées en haut, quatre par ligne. Faites-les glisser pour définir leur ordre.",
    sections: "Sections",
    sectionsDescription:
      "Panneaux sous les chiffres clés, dans une grille à 3 colonnes. Faites-les glisser pour définir leur ordre.",
    alwaysShown:
      "Un widget activé est toujours affiché, avec un état vide lorsqu'il n'y a pas de données.",
    userOnlyNote:
      "Prochains rendez-vous et Liquidités à investir concernent les clients d'un seul utilisateur ; ils se configurent donc uniquement dans l'onglet Tableau de bord utilisateur.",
    saveFailed: "Impossible d'enregistrer la modification.",
    unableToLoad: "Impossible de charger la configuration du tableau de bord.",
  },
  es_ES: {
    settings: "Configuración",
    dashboards: "Paneles",
    description:
      "Elige qué widgets ven los usuarios en cada panel y en qué orden. Los cambios se guardan al activar y arrastrar.",
    userDashboard: "Panel de usuario",
    performanceDashboard: "Panel de rendimiento",
    userVisibility: "Visible para todos los usuarios: sus propios clientes y su trabajo.",
    performanceVisibility: "Visible para usuarios con permiso de administrador.",
    complianceOnly: "Solo cumplimiento",
    userComplianceNote:
      "Estos widgets solo se muestran a los usuarios que también tienen permiso de cumplimiento.",
    performanceComplianceNote:
      "Estos widgets solo se muestran a los administradores que también tienen permiso de cumplimiento.",
    keyFigures: "Cifras clave",
    keyFiguresDescription:
      "Tarjetas pequeñas en una fila en la parte superior, cuatro por fila. Arrastra para definir su orden.",
    sections: "Secciones",
    sectionsDescription:
      "Paneles debajo de las cifras clave, en una cuadrícula de 3 columnas. Arrastra para definir su orden.",
    alwaysShown: "Un widget activado siempre se muestra, con un estado vacío cuando no hay datos.",
    userOnlyNote:
      "Próximas reuniones y Efectivo para invertir pertenecen a los clientes de un solo usuario, por lo que solo se configuran en la pestaña Panel de usuario.",
    saveFailed: "No se pudo guardar el cambio.",
    unableToLoad: "No se pudo cargar la configuración del panel.",
  },
  pt_BR: {
    settings: "Configurações",
    dashboards: "Painéis",
    description:
      "Escolha quais widgets os usuários veem em cada painel e em que ordem. As alterações são salvas conforme você ativa e arrasta.",
    userDashboard: "Painel do usuário",
    performanceDashboard: "Painel de desempenho",
    userVisibility: "Visível para todos os usuários — seus próprios clientes e trabalho.",
    performanceVisibility: "Visível para usuários com permissão de administrador.",
    complianceOnly: "Somente compliance",
    userComplianceNote:
      "Esses widgets são exibidos apenas para usuários que também têm permissão de compliance.",
    performanceComplianceNote:
      "Esses widgets são exibidos apenas para administradores que também têm permissão de compliance.",
    keyFigures: "Indicadores-chave",
    keyFiguresDescription:
      "Cartões pequenos em uma linha no topo, quatro por linha. Arraste para definir a ordem.",
    sections: "Seções",
    sectionsDescription:
      "Painéis abaixo dos indicadores-chave, em uma grade de 3 colunas. Arraste para definir a ordem.",
    alwaysShown: "Um widget ativado é sempre exibido, com um estado vazio quando não há dados.",
    userOnlyNote:
      "Próximas reuniões e Caixa para investir se referem aos clientes de um único usuário, por isso são configurados apenas na aba Painel do usuário.",
    saveFailed: "Não foi possível salvar a alteração.",
    unableToLoad: "Não foi possível carregar a configuração do painel.",
  },
  de_DE: {
    settings: "Einstellungen",
    dashboards: "Dashboards",
    description:
      "Legen Sie fest, welche Widgets Benutzer auf jedem Dashboard sehen und in welcher Reihenfolge. Änderungen werden beim Umschalten und Ziehen gespeichert.",
    userDashboard: "Benutzer-Dashboard",
    performanceDashboard: "Performance-Dashboard",
    userVisibility: "Für alle Benutzer sichtbar — ihre eigenen Kunden und Aufgaben.",
    performanceVisibility: "Für Benutzer mit Administratorberechtigung sichtbar.",
    complianceOnly: "Nur Compliance",
    userComplianceNote:
      "Diese Widgets werden nur Benutzern angezeigt, die zusätzlich die Compliance-Berechtigung haben.",
    performanceComplianceNote:
      "Diese Widgets werden nur Administratoren angezeigt, die zusätzlich die Compliance-Berechtigung haben.",
    keyFigures: "Kennzahlen",
    keyFiguresDescription:
      "Kleine Karten in einer Reihe oben, vier pro Reihe. Ziehen, um die Reihenfolge festzulegen.",
    sections: "Bereiche",
    sectionsDescription:
      "Bereiche unter den Kennzahlen, in einem Raster mit 3 Spalten. Ziehen, um die Reihenfolge festzulegen.",
    alwaysShown:
      "Ein aktiviertes Widget wird immer angezeigt, bei fehlenden Daten mit einem leeren Zustand.",
    userOnlyNote:
      "Nächste Termine und Liquidität zum Investieren beziehen sich auf die Kunden eines einzelnen Benutzers und werden daher nur im Tab Benutzer-Dashboard eingerichtet.",
    saveFailed: "Die Änderung konnte nicht gespeichert werden.",
    unableToLoad: "Die Dashboard-Einrichtung konnte nicht geladen werden.",
  },
  it_IT: {
    settings: "Impostazioni",
    dashboards: "Dashboard",
    description:
      "Scegli quali widget vedono gli utenti in ogni dashboard e in quale ordine. Le modifiche vengono salvate mentre attivi e trascini.",
    userDashboard: "Dashboard utente",
    performanceDashboard: "Dashboard delle performance",
    userVisibility: "Visibile a tutti gli utenti — i propri clienti e il proprio lavoro.",
    performanceVisibility: "Visibile agli utenti con permesso di amministratore.",
    complianceOnly: "Solo compliance",
    userComplianceNote:
      "Questi widget sono mostrati solo agli utenti che hanno anche il permesso di compliance.",
    performanceComplianceNote:
      "Questi widget sono mostrati solo agli amministratori che hanno anche il permesso di compliance.",
    keyFigures: "Dati chiave",
    keyFiguresDescription:
      "Piccole schede in una riga in alto, quattro per riga. Trascina per impostarne l'ordine.",
    sections: "Sezioni",
    sectionsDescription:
      "Pannelli sotto i dati chiave, in una griglia a 3 colonne. Trascina per impostarne l'ordine.",
    alwaysShown:
      "Un widget attivo viene sempre mostrato, con uno stato vuoto quando non ci sono dati.",
    userOnlyNote:
      "Prossime riunioni e Liquidità da investire riguardano i clienti di un solo utente, quindi si configurano solo nella scheda Dashboard utente.",
    saveFailed: "Impossibile salvare la modifica.",
    unableToLoad: "Impossibile caricare la configurazione della dashboard.",
  },
  ar: {
    settings: "الإعدادات",
    dashboards: "لوحات المعلومات",
    description:
      "اختر الأدوات التي يراها المستخدمون في كل لوحة معلومات وترتيبها. تُحفظ التغييرات أثناء التبديل والسحب.",
    userDashboard: "لوحة معلومات المستخدم",
    performanceDashboard: "لوحة معلومات الأداء",
    userVisibility: "مرئية لجميع المستخدمين — عملاؤهم وأعمالهم الخاصة.",
    performanceVisibility: "مرئية للمستخدمين الذين لديهم صلاحية المسؤول.",
    complianceOnly: "للامتثال فقط",
    userComplianceNote: "تظهر هذه الأدوات فقط للمستخدمين الذين لديهم أيضًا صلاحية الامتثال.",
    performanceComplianceNote: "تظهر هذه الأدوات فقط للمسؤولين الذين لديهم أيضًا صلاحية الامتثال.",
    keyFigures: "الأرقام الرئيسية",
    keyFiguresDescription: "بطاقات صغيرة في صف واحد في الأعلى، أربع في كل صف. اسحب لتحديد ترتيبها.",
    sections: "الأقسام",
    sectionsDescription: "لوحات أسفل الأرقام الرئيسية، ضمن شبكة من 3 أعمدة. اسحب لتحديد ترتيبها.",
    alwaysShown: "الأداة المفعّلة تُعرض دائمًا، مع حالة فارغة عند عدم وجود بيانات.",
    userOnlyNote:
      '"الاجتماعات القادمة" و"نقد متاح للاستثمار" تخص عملاء مستخدم واحد، لذا يتم إعدادها في تبويب لوحة معلومات المستخدم فقط.',
    saveFailed: "تعذّر حفظ التغيير.",
    unableToLoad: "تعذّر تحميل إعداد لوحة المعلومات.",
  },
  ja_JP: {
    settings: "設定",
    dashboards: "ダッシュボード",
    description:
      "各ダッシュボードでユーザーに表示するウィジェットとその順序を選択します。切り替えやドラッグの操作に合わせて変更が保存されます。",
    userDashboard: "ユーザーダッシュボード",
    performanceDashboard: "パフォーマンスダッシュボード",
    userVisibility: "すべてのユーザーに表示されます（各自の顧客と業務）。",
    performanceVisibility: "管理者権限を持つユーザーに表示されます。",
    complianceOnly: "コンプライアンスのみ",
    userComplianceNote: "のウィジェットは、コンプライアンス権限も持つユーザーにのみ表示されます。",
    performanceComplianceNote:
      "のウィジェットは、コンプライアンス権限も持つ管理者にのみ表示されます。",
    keyFigures: "主要指標",
    keyFiguresDescription: "上部に 1 行 4 枚で並ぶ小さなカードです。ドラッグして順序を設定します。",
    sections: "セクション",
    sectionsDescription:
      "主要指標の下に 3 列グリッドで並ぶパネルです。ドラッグして順序を設定します。",
    alwaysShown: "有効にしたウィジェットは常に表示され、データがない場合は空の状態で表示されます。",
    userOnlyNote:
      "「今後の面談」と「投資可能な現金」は 1 人のユーザーの顧客に紐づくため、「ユーザーダッシュボード」タブでのみ設定できます。",
    saveFailed: "変更を保存できませんでした。",
    unableToLoad: "ダッシュボードの設定を読み込めません。",
  },
  hi_IN: {
    settings: "सेटिंग्स",
    dashboards: "डैशबोर्ड",
    description:
      "चुनें कि उपयोगकर्ता हर डैशबोर्ड पर कौन-से विजेट और किस क्रम में देखें। स्विच करने और खींचने के साथ बदलाव सहेजे जाते हैं।",
    userDashboard: "उपयोगकर्ता डैशबोर्ड",
    performanceDashboard: "प्रदर्शन डैशबोर्ड",
    userVisibility: "सभी उपयोगकर्ताओं को दिखाई देता है — उनके अपने क्लाइंट और कार्य।",
    performanceVisibility: "एडमिन अनुमति वाले उपयोगकर्ताओं को दिखाई देता है।",
    complianceOnly: "केवल अनुपालन",
    userComplianceNote: "विजेट केवल उन उपयोगकर्ताओं को दिखाए जाते हैं जिनके पास अनुपालन अनुमति भी है।",
    performanceComplianceNote: "विजेट केवल उन एडमिन को दिखाए जाते हैं जिनके पास अनुपालन अनुमति भी है।",
    keyFigures: "मुख्य आँकड़े",
    keyFiguresDescription: "सबसे ऊपर एक पंक्ति में छोटे कार्ड, प्रति पंक्ति चार। क्रम तय करने के लिए खींचें।",
    sections: "सेक्शन",
    sectionsDescription: "मुख्य आँकड़ों के नीचे 3-कॉलम ग्रिड में पैनल। क्रम तय करने के लिए खींचें।",
    alwaysShown: "चालू किया गया विजेट हमेशा दिखाया जाता है; डेटा न होने पर वह खाली स्थिति में दिखता है।",
    userOnlyNote:
      "आगामी मीटिंग और निवेश योग्य नकद किसी एक उपयोगकर्ता के क्लाइंट से संबंधित हैं, इसलिए इन्हें केवल उपयोगकर्ता डैशबोर्ड टैब पर सेट किया जाता है।",
    saveFailed: "बदलाव सहेजा नहीं जा सका।",
    unableToLoad: "डैशबोर्ड सेटअप लोड नहीं हो सका।",
  },
  zh_CN: {
    settings: "设置",
    dashboards: "仪表板",
    description: "选择用户在每个仪表板上看到的小组件及其顺序。切换和拖动时会自动保存更改。",
    userDashboard: "用户仪表板",
    performanceDashboard: "业绩仪表板",
    userVisibility: "对所有用户可见——显示其各自的客户和工作。",
    performanceVisibility: "对拥有管理员权限的用户可见。",
    complianceOnly: "仅限合规",
    userComplianceNote: "小组件仅向同时拥有合规权限的用户显示。",
    performanceComplianceNote: "小组件仅向同时拥有合规权限的管理员显示。",
    keyFigures: "关键指标",
    keyFiguresDescription: "顶部一行的小卡片，每行四个。拖动可设置顺序。",
    sections: "板块",
    sectionsDescription: "关键指标下方的面板，按三列网格排列。拖动可设置顺序。",
    alwaysShown: "已启用的小组件始终显示，无数据时显示为空状态。",
    userOnlyNote:
      "“即将召开的会议”和“可投资现金”属于单个用户的客户，因此只能在“用户仪表板”选项卡中设置。",
    saveFailed: "无法保存更改。",
    unableToLoad: "无法加载仪表板设置。",
  },
  zh_TW: {
    settings: "設定",
    dashboards: "儀表板",
    description: "選擇使用者在每個儀表板上看到的小工具及其順序。切換與拖曳時會自動儲存變更。",
    userDashboard: "使用者儀表板",
    performanceDashboard: "績效儀表板",
    userVisibility: "所有使用者皆可見——顯示其各自的客戶與工作。",
    performanceVisibility: "具管理員權限的使用者可見。",
    complianceOnly: "僅限合規",
    userComplianceNote: "小工具僅對同時具有合規權限的使用者顯示。",
    performanceComplianceNote: "小工具僅對同時具有合規權限的管理員顯示。",
    keyFigures: "關鍵指標",
    keyFiguresDescription: "頂端一列的小卡片，每列四張。拖曳即可設定順序。",
    sections: "區塊",
    sectionsDescription: "關鍵指標下方的面板，以三欄網格排列。拖曳即可設定順序。",
    alwaysShown: "已啟用的小工具一律會顯示，沒有資料時會顯示空白狀態。",
    userOnlyNote:
      "「即將舉行的會議」與「可投資現金」屬於單一使用者的客戶，因此只能在「使用者儀表板」分頁中設定。",
    saveFailed: "無法儲存變更。",
    unableToLoad: "無法載入儀表板設定。",
  },
  nl_NL: {
    settings: "Instellingen",
    dashboards: "Dashboards",
    description:
      "Kies welke widgets gebruikers op elk dashboard zien, en in welke volgorde. Wijzigingen worden opgeslagen terwijl je schakelt en sleept.",
    userDashboard: "Gebruikersdashboard",
    performanceDashboard: "Prestatiedashboard",
    userVisibility: "Zichtbaar voor alle gebruikers — hun eigen klanten en werk.",
    performanceVisibility: "Zichtbaar voor gebruikers met beheerdersrechten.",
    complianceOnly: "Alleen compliance",
    userComplianceNote:
      "Widgets met dit label worden alleen getoond aan gebruikers die ook compliancerechten hebben.",
    performanceComplianceNote:
      "Widgets met dit label worden alleen getoond aan beheerders die ook compliancerechten hebben.",
    keyFigures: "Kerncijfers",
    keyFiguresDescription:
      "Kleine kaarten op één rij bovenaan, vier per rij. Sleep om de volgorde te bepalen.",
    sections: "Secties",
    sectionsDescription:
      "Panelen onder de kerncijfers, in een raster van 3 kolommen. Sleep om de volgorde te bepalen.",
    alwaysShown:
      "Een ingeschakelde widget wordt altijd getoond, met een lege weergave als er geen gegevens zijn.",
    userOnlyNote:
      "Komende afspraken en Te beleggen liquiditeit horen bij de klanten van één gebruiker en worden daarom alleen op het tabblad Gebruikersdashboard ingesteld.",
    saveFailed: "Kan de wijziging niet opslaan.",
    unableToLoad: "Kan de dashboardinstellingen niet laden.",
  },
  nb_NO: {
    settings: "Innstillinger",
    dashboards: "Dashbord",
    description:
      "Velg hvilke widgeter brukerne ser på hvert dashbord, og i hvilken rekkefølge. Endringer lagres når du slår av og på og drar.",
    userDashboard: "Brukerdashbord",
    performanceDashboard: "Resultatdashbord",
    userVisibility: "Synlig for alle brukere — deres egne kunder og arbeid.",
    performanceVisibility: "Synlig for brukere med admin-tilgang.",
    complianceOnly: "Kun compliance",
    userComplianceNote: "-widgeter vises bare for brukere som også har compliance-tilgang.",
    performanceComplianceNote:
      "-widgeter vises bare for administratorer som også har compliance-tilgang.",
    keyFigures: "Nøkkeltall",
    keyFiguresDescription:
      "Små kort på én rad øverst, fire per rad. Dra for å bestemme rekkefølgen.",
    sections: "Seksjoner",
    sectionsDescription:
      "Paneler under nøkkeltallene, i et rutenett med 3 kolonner. Dra for å bestemme rekkefølgen.",
    alwaysShown:
      "En widget som er slått på vises alltid, med en tom tilstand når det ikke finnes data.",
    userOnlyNote:
      "Neste møter og Kontanter å investere gjelder én brukers kunder, så de settes opp bare på fanen Brukerdashbord.",
    saveFailed: "Kunne ikke lagre endringen.",
    unableToLoad: "Kunne ikke laste dashbordoppsettet.",
  },
  sv_SE: {
    settings: "Inställningar",
    dashboards: "Dashboards",
    description:
      "Välj vilka widgetar användarna ser på varje dashboard och i vilken ordning. Ändringar sparas när du slår på/av och drar.",
    userDashboard: "Användardashboard",
    performanceDashboard: "Resultatdashboard",
    userVisibility: "Synlig för alla användare — deras egna kunder och arbete.",
    performanceVisibility: "Synlig för användare med administratörsbehörighet.",
    complianceOnly: "Endast compliance",
    userComplianceNote:
      "Widgetar med den här märkningen visas bara för användare som också har compliance-behörighet.",
    performanceComplianceNote:
      "Widgetar med den här märkningen visas bara för administratörer som också har compliance-behörighet.",
    keyFigures: "Nyckeltal",
    keyFiguresDescription:
      "Små kort i en rad högst upp, fyra per rad. Dra för att ställa in ordningen.",
    sections: "Sektioner",
    sectionsDescription:
      "Paneler under nyckeltalen, i ett rutnät med 3 kolumner. Dra för att ställa in ordningen.",
    alwaysShown: "En aktiverad widget visas alltid, med ett tomt läge när det saknas data.",
    userOnlyNote:
      "Kommande möten och Likvida medel att investera gäller en användares kunder, så de ställs bara in på fliken Användardashboard.",
    saveFailed: "Det gick inte att spara ändringen.",
    unableToLoad: "Det gick inte att läsa in dashboardinställningarna.",
  },
  fi_FI: {
    settings: "Asetukset",
    dashboards: "Koontinäytöt",
    description:
      "Valitse, mitkä widgetit käyttäjät näkevät kullakin koontinäytöllä ja missä järjestyksessä. Muutokset tallentuvat, kun kytket widgetejä päälle tai pois ja vedät niitä.",
    userDashboard: "Käyttäjän koontinäyttö",
    performanceDashboard: "Tuloskoontinäyttö",
    userVisibility: "Näkyy kaikille käyttäjille — heidän omat asiakkaansa ja työnsä.",
    performanceVisibility: "Näkyy käyttäjille, joilla on pääkäyttäjän oikeus.",
    complianceOnly: "Vain compliance",
    userComplianceNote:
      "Näin merkityt widgetit näkyvät vain käyttäjille, joilla on myös compliance-oikeus.",
    performanceComplianceNote:
      "Näin merkityt widgetit näkyvät vain pääkäyttäjille, joilla on myös compliance-oikeus.",
    keyFigures: "Tunnusluvut",
    keyFiguresDescription:
      "Pienet kortit yhdellä rivillä ylhäällä, neljä riviä kohden. Määritä järjestys vetämällä.",
    sections: "Osiot",
    sectionsDescription:
      "Paneelit tunnuslukujen alapuolella 3 sarakkeen ruudukossa. Määritä järjestys vetämällä.",
    alwaysShown: "Käyttöön otettu widget näytetään aina – tyhjänä, jos tietoja ei ole.",
    userOnlyNote:
      "Tulevat tapaamiset ja Sijoitettava käteinen koskevat yhden käyttäjän asiakkaita, joten ne määritetään vain Käyttäjän koontinäyttö -välilehdellä.",
    saveFailed: "Muutosta ei voitu tallentaa.",
    unableToLoad: "Koontinäytön asetuksia ei voitu ladata.",
  },
  da_DK: {
    settings: "Indstillinger",
    dashboards: "Dashboards",
    description:
      "Vælg, hvilke widgets brugerne ser på hvert dashboard, og i hvilken rækkefølge. Ændringer gemmes, når du slår til og fra og trækker.",
    userDashboard: "Brugerdashboard",
    performanceDashboard: "Resultatdashboard",
    userVisibility: "Synligt for alle brugere — deres egne kunder og arbejde.",
    performanceVisibility: "Synligt for brugere med administratorrettighed.",
    complianceOnly: "Kun compliance",
    userComplianceNote:
      "Widgets med denne markering vises kun for brugere, der også har compliance-rettighed.",
    performanceComplianceNote:
      "Widgets med denne markering vises kun for administratorer, der også har compliance-rettighed.",
    keyFigures: "Nøgletal",
    keyFiguresDescription:
      "Små kort på én række øverst, fire pr. række. Træk for at bestemme rækkefølgen.",
    sections: "Sektioner",
    sectionsDescription:
      "Paneler under nøgletallene i et gitter med 3 kolonner. Træk for at bestemme rækkefølgen.",
    alwaysShown:
      "En widget, der er slået til, vises altid – med en tom visning, når der ikke er data.",
    userOnlyNote:
      "Kommende møder og Kontanter til investering hører til én brugers kunder, så de sættes kun op på fanen Brugerdashboard.",
    saveFailed: "Kunne ikke gemme ændringen.",
    unableToLoad: "Kunne ikke indlæse dashboardopsætningen.",
  },
  is_IS: {
    settings: "Stillingar",
    dashboards: "Mælaborð",
    description:
      "Veldu hvaða græjur notendur sjá á hverju mælaborði og í hvaða röð. Breytingar vistast um leið og þú kveikir, slekkur og dregur.",
    userDashboard: "Notendamælaborð",
    performanceDashboard: "Árangursmælaborð",
    userVisibility: "Sýnilegt öllum notendum — þeirra eigin viðskiptavinir og vinna.",
    performanceVisibility: "Sýnilegt notendum með stjórnandaheimild.",
    complianceOnly: "Aðeins regluvarsla",
    userComplianceNote:
      "Græjur með þessu merki birtast aðeins notendum sem einnig hafa regluvörsluheimild.",
    performanceComplianceNote:
      "Græjur með þessu merki birtast aðeins stjórnendum sem einnig hafa regluvörsluheimild.",
    keyFigures: "Lykiltölur",
    keyFiguresDescription:
      "Lítil spjöld í einni röð efst, fjögur í röð. Dragðu til að ákveða röðina.",
    sections: "Hlutar",
    sectionsDescription:
      "Spjöld fyrir neðan lykiltölurnar, í hnitaneti með 3 dálkum. Dragðu til að ákveða röðina.",
    alwaysShown: "Græja sem kveikt er á birtist alltaf, tóm ef engin gögn eru til.",
    userOnlyNote:
      "Næstu fundir og Reiðufé til fjárfestingar tilheyra viðskiptavinum eins notanda, svo þau eru aðeins stilltar á flipanum Notendamælaborð.",
    saveFailed: "Ekki tókst að vista breytinguna.",
    unableToLoad: "Ekki tókst að hlaða uppsetningu mælaborðsins.",
  },
};
